import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { api } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { StareLista } from '../../components/ui/StareLista'
import { LUNI_SCURT, dataScurta, lei } from './comun'
import { descarcaPdf, type Fisa, type Sold } from './ClientPage'
import { FisaContPdf } from './FisaContPdf'

// ─── Facturi și încasări ───────────────────────────────────────────────────────

interface Facturi {
  deIncasat: number | null
  restant: number | null
  sold: Sold | null
  faraCif: boolean
  /** Ultima zi acoperita de extrasele bancare. */
  extraseLa: string | null
  platiDuble: { suma: number; prima: string; aDoua: string }[]
  documente: {
    docNo: string
    data: string | null
    scadenta: string | null
    ramas: number
    pesteTermen: boolean
    faraTermen: boolean
    zile: number | null
  }[]
  incasari: { id: number; suma: number; data: string; detalii: string | null }[]
  client: { nume: string; cif: string | null }
}

export function FilaFacturi({ id, poatePdf }: { id: number; poatePdf: boolean }) {
  const q = useQuery<Facturi>({
    queryKey: ['clienti', 'facturi', id],
    queryFn: async () => (await api.get(`/clienti/${id}/facturi`, { timeout: 90_000 })).data,
  })
  const d = q.data
  const inPlus = (d?.sold?.platitInPlus ?? 0) > 0
  const pdf = (cale: string, nume: string) =>
    d &&
    descarcaPdf(cale, { name: d.client.nume, ...(d.client.cif ? { cif: d.client.cif } : {}) }, nume)
  return (
    <StareLista interogare={q} gol={false} mesajGol="">
      {d && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <section
            aria-label="Facturi deschise"
            className="grid content-start gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-[15px] font-bold text-b4l-text">Facturi deschise</h2>
              <span className="text-corp text-b4l-text-muted">
                {inPlus ? (
                  <>
                    Plătit în plus{' '}
                    <b className="text-b4l-green-dark">{lei(d.sold!.platitInPlus, 2)}</b> · sold
                    creditor
                  </>
                ) : (
                  <>
                    De încasat <b className="text-b4l-text">{lei(d.deIncasat, 2)}</b>
                  </>
                )}
                {(d.restant ?? 0) > 0.5 && (
                  <>
                    {' '}
                    · restant <b className="text-b4l-red-dark">{lei(d.restant, 2)}</b>
                  </>
                )}
              </span>
            </div>
            {d.sold && d.sold.nealocateEa > 0.5 && (
              <p className="rounded-lg bg-b4l-bg-soft px-3 py-2 text-corp text-b4l-text">
                Încasări primite, încă nealocate în EA:{' '}
                <b className="tabular-nums">{lei(d.sold.nealocateEa, 2)}</b>. Aici sunt deja
                scăzute; în contabilitate soldul apare încă {lei(d.sold.soldEa, 2)} până le stinge
                contabilul pe facturi.
              </p>
            )}
            {d.sold && d.sold.diferentaLista > 0.5 && (
              <p className="rounded-lg bg-b4l-bg-soft px-3 py-2 text-corp text-b4l-text">
                Din soldul de încasat,{' '}
                <b className="tabular-nums">{lei(d.sold.diferentaLista, 2)}</b> nu stau încă pe o
                factură deschisă în EA (factură încă necontată sau plată trecută doar în
                contabilitate).
              </p>
            )}
            {d.platiDuble.map((p) => (
              <p
                key={`${p.prima}-${p.aDoua}-${p.suma}`}
                className="rounded-lg bg-b4l-amber-light px-3 py-2 text-corp text-b4l-amber-dark"
              >
                <b>Posibilă plată dublă:</b> {lei(p.suma, 2)} pe {dataScurta(p.prima)} și din nou pe{' '}
                {dataScurta(p.aDoua)}, fără o factură nouă care s-o justifice. De verificat cu
                clientul.
              </p>
            ))}
            {poatePdf && (
              <div className="flex flex-wrap items-start gap-2">
                <FisaContPdf client={d.client} extraseLa={d.extraseLa} />
                <Button
                  variant="secondary"
                  onClick={() =>
                    pdf('/agents/client-sold-facturi/pdf', `sold-facturi-${d.client.nume}.pdf`)
                  }
                >
                  Soldul pe facturi PDF
                </Button>
              </div>
            )}
            {d.faraCif ? (
              <p className="text-corp text-b4l-text-muted">
                Clientul nu are cod fiscal, deci facturile lui din ExpertAccounts nu pot fi
                asociate.
              </p>
            ) : d.documente.length === 0 ? (
              <p className="text-corp text-b4l-text-muted">
                {inPlus
                  ? 'Nicio factură deschisă: clientul a plătit în plus.'
                  : 'Nicio factură deschisă.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse text-corp">
                  <thead>
                    <tr className="text-left text-mic text-b4l-text-muted">
                      <th scope="col" className="py-1.5 font-medium">
                        Factura
                      </th>
                      <th scope="col" className="py-1.5 font-medium">
                        Emisă
                      </th>
                      <th scope="col" className="py-1.5 font-medium">
                        Scadența
                      </th>
                      <th scope="col" className="py-1.5 text-right font-medium">
                        Rămas
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.documente.map((x) => (
                      <tr key={x.docNo} className={x.pesteTermen ? 'text-b4l-red-dark' : ''}>
                        <td className="border-t border-b4l-border py-2 font-semibold">{x.docNo}</td>
                        <td className="border-t border-b4l-border py-2">{dataScurta(x.data)}</td>
                        <td className="border-t border-b4l-border py-2">
                          {x.faraTermen ? 'fără termen' : dataScurta(x.scadenta)}
                          {x.pesteTermen && x.zile != null && <b> · {x.zile} zile peste</b>}
                        </td>
                        <td className="border-t border-b4l-border py-2 text-right tabular-nums">
                          {lei(x.ramas, 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-mic text-b4l-text-dim">
              Aceeași socoteală ca Tabloul de bord vânzări și ca fișa de cont PDF: încasările se
              sting de la cea mai veche factură.
              {d.extraseLa &&
                ` Încasări din extrase până la ${d.extraseLa.split('-').reverse().join('.')}.`}
            </p>
          </section>
          <section
            aria-label="Încasări"
            className="grid content-start gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
          >
            <h2 className="text-[15px] font-bold text-b4l-text">Încasări — ultimele 4 luni</h2>
            {d.incasari.length === 0 ? (
              <p className="text-corp text-b4l-text-muted">Nicio încasare găsită în extrase.</p>
            ) : (
              <ul className="grid gap-0">
                {d.incasari.map((b) => (
                  <li
                    key={b.id}
                    className="flex justify-between gap-3 border-t border-b4l-border py-2 text-corp first:border-t-0"
                  >
                    <span className="min-w-0">
                      <span className="text-b4l-text">{dataScurta(b.data)}</span>
                      {b.detalii && (
                        <span className="block truncate text-mic text-b4l-text-muted">
                          {b.detalii}
                        </span>
                      )}
                    </span>
                    <b className="tabular-nums text-b4l-green-dark">{lei(b.suma, 2)}</b>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </StareLista>
  )
}

// ─── Sell-in / Sell-out ────────────────────────────────────────────────────────

interface Vanzari {
  lunile: { luna: string; qtyIn: number; qtyOut: number; valIn: number; faraRaport: boolean }[]
  produse: {
    productId: number
    sku: string
    nume: string
    qtyIn: number
    qtyOut: number
    valIn: number
    pctOut: number | null
    pret: number | null
  }[]
  trimiteSellout: boolean
}

export function FilaVanzari({ id }: { id: number }) {
  const [luni, setLuni] = useState(12)
  const q = useQuery<Vanzari>({
    queryKey: ['clienti', 'vanzari', id, luni],
    queryFn: async () =>
      (await api.get(`/clienti/${id}/vanzari`, { params: { luni }, timeout: 90_000 })).data,
  })
  const d = q.data
  const max = Math.max(1, ...(d?.lunile ?? []).flatMap((l) => [l.qtyIn, l.qtyOut]))
  return (
    <StareLista interogare={q} gol={false} mesajGol="">
      {d && (
        <div className="grid gap-4">
          <section
            aria-label="Pe luni"
            className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-[15px] font-bold text-b4l-text">
                  Sell-in / Sell-out, în bucăți
                </h2>
                <p className="text-mic text-b4l-text-muted">
                  Sell-in = facturat de noi (EA). Sell-out = vândut de client, din rapoartele lui
                  {d.trimiteSellout ? '.' : ' — clientul n-a trimis încă niciun raport.'}
                </p>
              </div>
              <select
                aria-label="Perioada"
                value={luni}
                onChange={(e) => setLuni(Number(e.target.value))}
                className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp text-b4l-text"
              >
                <option value={6}>Ultimele 6 luni</option>
                <option value={12}>Ultimele 12 luni</option>
                <option value={24}>Ultimele 24 de luni</option>
              </select>
            </div>
            <div className="flex gap-4 text-mic text-b4l-text-muted" aria-hidden="true">
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-b4l-blue" />
                Sell-in
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-b4l-amber" />
                Sell-out
              </span>
              {d.trimiteSellout && (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm border border-dashed border-b4l-text-dim" />
                  raport lipsă
                </span>
              )}
            </div>
            <div
              role="img"
              aria-label={`Sell-in și sell-out pe ultimele ${luni} luni`}
              className="flex h-[200px] items-end gap-2 overflow-x-auto"
            >
              {d.lunile.map((l) => (
                <div
                  key={l.luna}
                  className="flex min-w-[34px] flex-1 flex-col items-center gap-1.5"
                  title={`${l.luna}: sell-in ${l.qtyIn} buc, sell-out ${l.faraRaport ? 'raport lipsă' : `${l.qtyOut} buc`}`}
                >
                  <div className="flex h-[170px] items-end gap-[3px]">
                    <span
                      className="w-3 rounded-t bg-b4l-blue"
                      style={{ height: `${(l.qtyIn / max) * 165}px` }}
                    />
                    {l.faraRaport ? (
                      <span className="h-[50px] w-3 rounded-t border border-dashed border-b4l-text-dim" />
                    ) : (
                      <span
                        className="w-3 rounded-t bg-b4l-amber"
                        style={{ height: `${(l.qtyOut / max) * 165}px` }}
                      />
                    )}
                  </div>
                  <span className="text-[11px] text-b4l-text-muted">
                    {LUNI_SCURT[Number(l.luna.slice(5)) - 1]}
                  </span>
                </div>
              ))}
            </div>
          </section>
          <section
            aria-label="Pe produs"
            className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface"
          >
            <table className="w-full min-w-[640px] border-collapse text-corp">
              <thead>
                <tr className="text-left text-mic text-b4l-text-muted">
                  <th scope="col" className="px-3.5 py-2.5 font-medium">
                    Produs
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-medium">
                    Sell-in
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-medium">
                    Valoare
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-medium">
                    Sell-out
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-medium">
                    Vândut din cumpărat
                  </th>
                  <th scope="col" className="px-3.5 py-2.5 text-right font-medium">
                    Stoc estimat la client
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.produse.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3.5 py-6 text-center text-b4l-text-muted">
                      Nimic în perioada aleasă.
                    </td>
                  </tr>
                )}
                {d.produse.map((p) => (
                  <tr key={p.productId}>
                    <td className="border-t border-b4l-border px-3.5 py-2">
                      {p.nume}
                      <span className="block text-mic text-b4l-text-dim">{p.sku}</span>
                    </td>
                    <td className="border-t border-b4l-border px-2 py-2 text-right tabular-nums">
                      {p.qtyIn.toLocaleString('ro-RO')}
                    </td>
                    <td className="border-t border-b4l-border px-2 py-2 text-right tabular-nums">
                      {lei(p.valIn)}
                    </td>
                    <td className="border-t border-b4l-border px-2 py-2 text-right tabular-nums">
                      {d.trimiteSellout ? p.qtyOut.toLocaleString('ro-RO') : '—'}
                    </td>
                    <td className="border-t border-b4l-border px-2 py-2 text-right text-b4l-text-muted">
                      {p.pctOut != null ? `${p.pctOut}%` : '—'}
                    </td>
                    <td className="border-t border-b4l-border px-3.5 py-2 text-right text-b4l-text-muted">
                      {d.trimiteSellout
                        ? `≈ ${Math.max(0, p.qtyIn - p.qtyOut).toLocaleString('ro-RO')} buc`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </StareLista>
  )
}

// ─── Prețuri ───────────────────────────────────────────────────────────────────

interface Preturi {
  preturi: {
    sku: string
    nume: string
    lista: number | null
    client: number | null
    discount: number | null
  }[]
  cititeLa: string | null
}

export function FilaPreturi({ id }: { id: number }) {
  const [cauta, setCauta] = useState('')
  const [doarAleLui, setDoarAleLui] = useState(true)
  const q = useQuery<Preturi>({
    queryKey: ['clienti', 'preturi', id],
    queryFn: async () => (await api.get(`/clienti/${id}/preturi`, { timeout: 60_000 })).data,
  })
  const randuri = useMemo(() => {
    const t = cauta.trim().toLowerCase()
    return (q.data?.preturi ?? [])
      .filter((p) => !doarAleLui || p.client != null)
      .filter((p) => !t || p.nume.toLowerCase().includes(t) || p.sku.toLowerCase().includes(t))
  }, [q.data, cauta, doarAleLui])
  return (
    <div className="grid gap-3">
      <p className="text-corp text-b4l-text-muted">
        Prețurile clientului din ExpertAccounts — se schimbă acolo, nu aici. Aceleași apar la
        Comandă nouă.
        {q.data?.cititeLa &&
          ` Citite din ExpertAccounts: ${new Date(q.data.cititeLa).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })}.`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[240px] flex-1 items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-b4l-text-dim">
          <Search size={15} aria-hidden="true" />
          <input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            placeholder="Caută produsul"
            aria-label="Caută produsul"
            className="min-w-0 flex-1 bg-transparent text-corp text-b4l-text outline-none"
          />
        </label>
        <label className="flex items-center gap-2 text-corp text-b4l-text">
          <input
            type="checkbox"
            checked={doarAleLui}
            onChange={(e) => setDoarAleLui(e.target.checked)}
          />
          Doar cu preț negociat
        </label>
      </div>
      <StareLista
        interogare={q}
        gol={randuri.length === 0}
        mesajGol={
          doarAleLui
            ? 'Clientul n-are prețuri negociate în ExpertAccounts: se facturează la prețul de listă.'
            : 'Niciun produs.'
        }
      >
        <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
          <table className="w-full min-w-[560px] border-collapse text-corp">
            <thead>
              <tr className="text-left text-mic text-b4l-text-muted">
                <th scope="col" className="px-3.5 py-2.5 font-medium">
                  Produs
                </th>
                <th scope="col" className="px-2 py-2.5 text-right font-medium">
                  Preț listă
                </th>
                <th scope="col" className="px-2 py-2.5 text-right font-medium">
                  Prețul lui
                </th>
                <th scope="col" className="px-3.5 py-2.5 text-right font-medium">
                  Discount
                </th>
              </tr>
            </thead>
            <tbody>
              {randuri.map((p) => (
                <tr key={p.sku}>
                  <td className="border-t border-b4l-border px-3.5 py-2">
                    {p.nume}
                    <span className="block text-mic text-b4l-text-dim">{p.sku}</span>
                  </td>
                  <td className="border-t border-b4l-border px-2 py-2 text-right tabular-nums text-b4l-text-muted">
                    {p.lista != null ? lei(p.lista, 2) : '—'}
                  </td>
                  <td className="border-t border-b4l-border px-2 py-2 text-right font-semibold tabular-nums">
                    {p.client != null ? lei(p.client, 2) : '—'}
                  </td>
                  <td className="border-t border-b4l-border px-3.5 py-2 text-right text-b4l-text-muted">
                    {p.discount != null ? `−${p.discount.toLocaleString('ro-RO')}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StareLista>
      <p className="text-mic text-b4l-text-dim">
        Fără „Prețul lui” = se facturează la prețul de listă.
      </p>
    </div>
  )
}

// ─── Puncte de lucru și contacte ──────────────────────────────────────────────

export function FilaPuncte({
  f,
  poateEdita,
  laEditare,
}: {
  f: Fisa
  poateEdita: boolean
  laEditare: () => void
}) {
  const c = f.client
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <section
        aria-label="Contact"
        className="grid content-start gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[15px] font-bold text-b4l-text">Date de contact</h2>
          {poateEdita && (
            <Button variant="secondary" onClick={laEditare}>
              Editează
            </Button>
          )}
        </div>
        <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-2 text-corp">
          <dt className="text-b4l-text-muted">Persoană de contact</dt>
          <dd>{c.persoanaContact ?? '—'}</dd>
          <dt className="text-b4l-text-muted">Telefon</dt>
          <dd>{c.telefon ?? '—'}</dd>
          <dt className="text-b4l-text-muted">Email</dt>
          <dd className="break-words">{c.email ?? '—'}</dd>
          <dt className="text-b4l-text-muted">Adresa</dt>
          <dd>{c.adresa ?? '—'}</dd>
          <dt className="text-b4l-text-muted">Manager de cont</dt>
          <dd>{c.managerCont ?? '—'}</dd>
          <dt className="text-b4l-text-muted">Observații</dt>
          <dd className="whitespace-pre-line">{c.observatii ?? '—'}</dd>
        </dl>
      </section>
      <section
        aria-label="Puncte de lucru"
        className="grid content-start gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
      >
        <h2 className="text-[15px] font-bold text-b4l-text">Puncte de lucru · {f.puncte.length}</h2>
        {f.puncte.length === 0 ? (
          <p className="text-corp text-b4l-text-muted">Niciun punct de lucru în ExpertAccounts.</p>
        ) : (
          <ul className="grid gap-2">
            {f.puncte.map((p) => (
              <li key={p.id} className="rounded-lg border border-b4l-border px-3 py-2 text-corp">
                <b className="font-semibold text-b4l-text">{p.branchName}</b>
                <span className="block text-mic text-b4l-text-muted">
                  {[p.address, p.city, p.state].filter(Boolean).join(', ') || 'fără adresă'}
                </span>
                {(p.person || p.tel || p.email) && (
                  <span className="block text-mic text-b4l-text-muted">
                    {[p.person, p.tel, p.email].filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-mic text-b4l-text-dim">
          Punctele de lucru vin din ExpertAccounts și se adaugă de la Comandă nouă.
        </p>
      </section>
    </div>
  )
}
